/**
 * @fileoverview Context for managing modal dialogs in the Storyteller web application.
 * This context provides a centralized state and API for opening and closing modal dialogs.
 *
 * @example
 * // Basic usage
 * <ModalContext>
 *     <Button onClick={() => modalAPI.open({ render: () => <div>Modal Content</div> })}>
 *         Open Modal
 *     </Button>
 * </ModalContext>
 */

import { createContext } from 'react'

/**
 * @interface ModalConfig
 * @description Configuration object for the Modal component.
 * @property {Function} render - A function that returns the modal content.
 * @property {boolean} canClose - Whether the modal can be closed by clicking outside.
 * @property {Function} onCancel - A function to handle the cancel action.
 * @property {Function} onConfirm - A function to handle the confirm action.
 * @property {Function} onClose - A function to handle the close action.
 */
export interface ModalConfig {
    render: (config: ModalConfig) => JSX.Element
    canClose?: boolean
    onCancel?: () => Promise<unknown>
    onConfirm?: () => Promise<unknown>
    onClose?: () => Promise<unknown>
}

/**
 * @interface ModalAPI
 * @description API for managing modal dialogs.
 * @property {Function} open - A function to open a modal dialog.
 * @property {Function} close - A function to close the currently open modal dialog.
 */
export interface ModalAPI {
    open: (config: ModalConfig) => void
    close: () => void
}

export const ModalContext = createContext<ModalAPI>(undefined)
